import React from 'react';
import { useGetLoadGroupsListQuery } from '@fiji/common/src/features/loadGroups/loadGroupsApi';
import { useScrollablePagination } from './useScrollablePagination';

type UseLoadGroupsScrollableListOptions = {
    containerRef: React.RefObject<HTMLDivElement | null>;
    pageSize?: number;
    deviceId?: string;
    isMultiDevice?: boolean;
    forceListWithoutDevice?: boolean;
    modelCategory?: string[];
    globalSearch?: any;
    sort?: any;
    enabled?: boolean;
};

type UseLoadGroupsScrollableListResult = {
    page: number;
    listRecords: any[];
    isLoading: boolean;
    isFetching: boolean;
    total?: number;
    rawResponse?: any;
    reset: () => void;
};

export const useLoadGroupsScrollableList = (
    options: UseLoadGroupsScrollableListOptions
): UseLoadGroupsScrollableListResult => {
    const {
        containerRef,
        pageSize = 25,
        deviceId,
        isMultiDevice,
        forceListWithoutDevice,
        modelCategory,
        globalSearch,
        sort,
        enabled = true,
    } = options;
    const sortKey = React.useMemo(() => JSON.stringify(sort ?? {}), [sort]);
    const modelCategoryKey = React.useMemo(() => JSON.stringify(modelCategory ?? []), [modelCategory]);

    const skipListFetch = !enabled || (!deviceId && !isMultiDevice && !forceListWithoutDevice);
    const [page, setPage] = React.useState(0);

    const {
        isLoading,
        isFetching,
        currentData: loadGroupLists,
    }: any = useGetLoadGroupsListQuery(
        {
            page,
            size: pageSize,
            ...(sort && { sort }),
            ...(deviceId && !isMultiDevice && { deviceId: [deviceId] }),
            ...(modelCategory?.length && { modelCategory }),
            ...(globalSearch?.length && { globalSearch }),
        },
        {
            skip: skipListFetch,
            refetchOnMountOrArgChange: true,
        }
    );

    const { reset } = useScrollablePagination({
        containerRef,
        pageSize,
        page,
        setPage,
        total: loadGroupLists?.data?.total ?? loadGroupLists?.data?.totalRecords,
        lastPageCount: loadGroupLists?.data?.records?.length,
        isLoading,
        isFetching,
        resetDeps: [globalSearch, deviceId, isMultiDevice, forceListWithoutDevice, sortKey, modelCategoryKey, enabled],
    });

    const [listRecords, setListRecords] = React.useState<any[]>([]);
    const mergeUniqueById = React.useCallback((prev: any[], next: any[]): any[] => {
        const merged = [...prev, ...next];
        return merged.reduce((acc: any[], current: any) => {
            if (!acc.find((item) => item?.id === current?.id)) acc.push(current);
            return acc;
        }, []);
    }, []);

    React.useEffect(() => {
        if (skipListFetch) {
            setListRecords([]);
            return;
        }
        const recordsPayload = loadGroupLists?.data?.records;
        const hasArrayRecordsPayload = Array.isArray(recordsPayload);
        const hasNullRecordsPayload = recordsPayload === null;

        if (!hasArrayRecordsPayload && !hasNullRecordsPayload) {
            // Keep the last successful list when current request returns an error payload.
            return;
        }

        const newRecords = hasArrayRecordsPayload ? recordsPayload : [];
        if (page === 0) {
            if (!isFetching) {
                setListRecords(newRecords);
            }
        } else if (newRecords.length) {
            setListRecords((prev) => mergeUniqueById(prev, newRecords));
        }
    }, [
        skipListFetch,
        loadGroupLists?.data?.records,
        page,
        isFetching,
        mergeUniqueById,
        globalSearch,
        deviceId,
        isMultiDevice,
        forceListWithoutDevice,
        sortKey,
        modelCategoryKey,
        enabled,
    ]);

    React.useEffect(() => {
        setListRecords([]);
    }, [deviceId, isMultiDevice, forceListWithoutDevice, sortKey, modelCategoryKey]);

    return {
        page,
        listRecords,
        isLoading,
        isFetching,
        total: loadGroupLists?.data?.total ?? loadGroupLists?.data?.totalRecords,
        rawResponse: loadGroupLists,
        reset,
    };
};
